import proofkitFilemakerService from './proofkitFilemakerService.js';

// Obtener el valor de la variable de entorno
const LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_FAMILIAS;
const PLANES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_PLANES;

// Mostrar advertencia si la variable no está configurada correctamente
if (!LAYOUT || LAYOUT === 'your_filemaker_layout_familias') {
  console.warn('Advertencia: REACT_APP_FILEMAKER_LAYOUT_FAMILIAS no está configurado correctamente. Valor actual:', LAYOUT);
}

export const getFamilias = async () => {
  try {
    // Verificar que LAYOUT esté definido
    if (!LAYOUT) {
      console.error('Error: REACT_APP_FILEMAKER_LAYOUT_FAMILIAS no está definido');
      throw new Error('Configuración de layout no disponible');
    }
    
    const response = await proofkitFilemakerService.getAllRecords(LAYOUT);
    const rows = Array.isArray(response?.data) ? response.data : [];

    if (rows.length === 0) {
      return [];
    }

    return rows.map(item => ({
      id: item.fieldData?.id,
      recordId: item.recordId,
      titulo: item.fieldData?.titulo,
      descripcion: item.fieldData?.descripcion,
      urlImagen: item.fieldData?.urlImagen,
      colorDestacado: item.fieldData?.colorDestacado,
      status: item.fieldData?.status,
      uuid: item.fieldData?.uuid,
      archived: item.fieldData?.archived
    }));
  } catch (error) {
    console.error('Error al obtener familias:', error);
    throw error;
  }
};

export const getFamiliaById = async (recordId) => {
  try {
    // Check if LAYOUT is defined
    if (!LAYOUT) {
      console.warn('Warning: REACT_APP_FILEMAKER_LAYOUT_FAMILIAS is not defined');
      return null;
    }
    
    const response = await proofkitFilemakerService.getRecord(LAYOUT, recordId);
    const familia = response?.data;

    if (!familia) {
      console.error(`No se encontró la familia con ID ${recordId}`);
      throw new Error(`No se encontró la familia con ID ${recordId}`);
    }

    return {
      id: familia.fieldData?.id,
      recordId: familia.recordId,
      titulo: familia.fieldData?.titulo,
      descripcion: familia.fieldData?.descripcion,
      urlImagen: familia.fieldData?.urlImagen,
      colorDestacado: familia.fieldData?.colorDestacado,
      status: familia.fieldData?.status,
      uuid: familia.fieldData?.uuid,
      archived: familia.fieldData?.archived
    };
  } catch (error) {
    console.error(`Error al obtener familia con ID ${recordId}:`, error);
    throw error;
  }
};

export const createFamilia = async (familiaData) => {
  try {
    // Transformar los datos para que coincidan con la estructura esperada por la API
    const apiData = {
      titulo: familiaData.titulo,
      descripcion: familiaData.descripcion,
      colorDestacado: familiaData.colorDestacado,
      status: familiaData.status || 'active',
      urlImagen: familiaData.urlImagen || 'https://yooldigital.com/assets/default.png'
    };
    
    return await proofkitFilemakerService.createRecord(LAYOUT, apiData);
  } catch (error) {
    console.error('Error al crear familia:', error);
    throw error;
  }
};

// Add archive functionality to updateFamilia
export const updateFamilia = async (recordId, familiaData) => {
  try {
    // Ensure we're only sending fields that are provided
    const apiData = {};
    
    // Map fields from familiaData to apiData
    if (familiaData.titulo !== undefined) apiData.titulo = familiaData.titulo; // Changed from nombre to titulo
    if (familiaData.descripcion !== undefined) apiData.descripcion = familiaData.descripcion;
    if (familiaData.urlImagen !== undefined) apiData.urlImagen = familiaData.urlImagen;
    if (familiaData.colorDestacado !== undefined) apiData.colorDestacado = familiaData.colorDestacado;
    if (familiaData.status !== undefined) apiData.status = familiaData.status;
    
    // Ensure archived is sent as a number (0 or 1)
    if (familiaData.archived !== undefined) {
      // If it's a boolean, convert to number
      if (typeof familiaData.archived === 'boolean') {
        apiData.archived = familiaData.archived ? 1 : 0;
      } else {
        // If it's already a number, use it directly
        apiData.archived = familiaData.archived;
      }
    }
    
    console.log('Sending update with data:', apiData); // Debug log

    return await proofkitFilemakerService.updateRecord(LAYOUT, recordId, apiData);
  } catch (error) {
    console.error(`Error updating familia with ID ${recordId}:`, error);
    throw error;
  }
};

export const deleteFamilia = async (recordId) => {
  try {
    return await proofkitFilemakerService.deleteRecord(LAYOUT, recordId);
  } catch (error) {
    console.error(`Error al eliminar familia con ID ${recordId}:`, error);
    throw error;
  }
};

export const getFamiliasWithPlanesCount = async () => {
  try {
    // Get all familias
    const familias = await getFamilias();
    
    // Verify that PLANES_LAYOUT is defined
    if (!PLANES_LAYOUT) {
      console.warn('Warning: REACT_APP_FILEMAKER_LAYOUT_PLANES is not defined');
      return familias.map(familia => ({
        ...familia,
        planesCount: 0
      }));
    }
    
    // Get all planes to count associations
    try {
      const planesResponse = await proofkitFilemakerService.getAllRecords(PLANES_LAYOUT);
      const planes = Array.isArray(planesResponse?.data) ? planesResponse.data : [];

      if (planes.length === 0) {
        return familias.map(familia => ({
          ...familia,
          planesCount: 0
        }));
      }
      
      // Count planes for each familia
      return familias.map(familia => {
        const planesCount = planes.filter(plan => 
          plan.fieldData?.familiaId === familia.id
        ).length;
        
        return {
          ...familia,
          planesCount
        };
      });
    } catch (error) {
      console.error('Error fetching planes data:', error);
      // Return familias with zero counts if planes fetch fails
      return familias.map(familia => ({
        ...familia,
        planesCount: 0
      }));
    }
  } catch (error) {
    console.error('Error getting familias with planes count:', error);
    throw error;
  }
};